import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../App";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Leaf, Sun, TreePine, Bug, Droplets, Calendar,
  ArrowRight, Sparkles, MapPin, Layers, Camera, BookOpen,
  Bell, Users, User, LogOut, Home, MessageCircle, Store, Paintbrush, Trophy,
  HelpCircle, Menu, X
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import LanguageSelector from "../components/LanguageSelector";
import GuidedTour from "../components/GuidedTour";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Vibrant Tropical Calypso Colors for Homepage
const tropical = {
  green: { primary: "#00A86B", vibrant: "#32DE84", dark: "#006B3C" },
  mango: { primary: "#FF9F1C", vibrant: "#FFD93D", light: "#FFBF69" },
  plum: { primary: "#9B59B6", vibrant: "#A855F7" },
  guava: { primary: "#FF6B6B", vibrant: "#FF4757" },
  coconut: { cream: "#FFFBEB" },
  papaya: { primary: "#FF7F50", light: "#FFDAB9" },
  passion: { primary: "#C71585", light: "#FFB6C1" }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const { t } = useLanguage();
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/site-settings`)
      .then(res => setSiteSettings(res.data))
      .catch(() => {});
  }, []);

  const features = [
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Location-Based Planning",
      description: "Enter your address or use the map to get climate-specific recommendations tailored to your exact location.",
      link: "/create"
    },
    {
      icon: <Sun className="w-8 h-8" />,
      title: "Solar Optimization",
      description: "Advanced calculations determine sun exposure throughout the year to maximize your garden's potential.",
      link: "/create"
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: "Vertical Layering",
      description: "Utilize every dimension with permaculture-inspired food forest layers from canopy to ground cover.",
      link: "/design"
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: "Plant Identifier",
      description: "Snap a photo of any plant and we'll identify it instantly with care tips.",
      link: "/identify"
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Garden Journal",
      description: "Track your garden's progress with photos, notes, and observations throughout the seasons.",
      link: "/journal"
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Seasonal Calendar",
      description: "Never miss a planting window with personalized seasonal task reminders and care schedules.",
      link: "/calendar"
    }
  ];

  const aesthetics = [
    { 
      name: "Food Forest", 
      icon: <span className="text-xl">🥭</span>, 
      color: "bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-lg shadow-green-500/30", 
      filter: "Europe",
      desc: "Mangoes, Avocados & More"
    },
    { 
      name: "Tropical Kitchen", 
      icon: <span className="text-xl">🍍</span>, 
      color: "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg shadow-orange-500/30", 
      filter: "South America",
      desc: "Pineapple, Papaya, Passion Fruit"
    },
    { 
      name: "Serenity Garden", 
      icon: <span className="text-xl">🌸</span>, 
      color: "bg-gradient-to-r from-pink-400 to-rose-500 text-white shadow-lg shadow-pink-500/30", 
      filter: "Asia",
      desc: "Zen & Peaceful"
    },
    { 
      name: "Pollinator Paradise", 
      icon: <span className="text-xl">🦋</span>, 
      color: "bg-gradient-to-r from-purple-400 to-violet-500 text-white shadow-lg shadow-purple-500/30", 
      filter: "North America",
      desc: "Bees, Butterflies & Birds"
    },
    { 
      name: "Island Harvest", 
      icon: <span className="text-xl">🥥</span>, 
      color: "bg-gradient-to-r from-cyan-400 to-teal-500 text-white shadow-lg shadow-cyan-500/30", 
      filter: "Oceania",
      desc: "Coconut, Banana, Breadfruit"
    },
    { 
      name: "Freedom Garden", 
      icon: <span className="text-xl">🌱</span>, 
      color: "bg-gradient-to-r from-lime-400 to-green-500 text-white shadow-lg shadow-lime-500/30", 
      filter: null,
      desc: "Indoor & Year-Round"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Guided Tour */}
      <GuidedTour 
        forceShow={showTour} 
        onComplete={() => setShowTour(false)} 
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Mobile Hamburger Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>
            
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="RootSpace.Pro" className="h-10 w-auto" />
            </Link>
          </div>
          <div className="flex items-center gap-6">
            <Link 
              to="/marketplace" 
              className="nav-link text-foreground/80 hover:text-primary font-medium hidden md:block"
              data-testid="marketplace-nav"
            >
              Seed Shop
            </Link>
            <Link 
              to="/design" 
              className="nav-link text-foreground/80 hover:text-primary font-medium hidden md:block"
              data-testid="design-nav"
            >
              Design Studio
            </Link>
            <Link 
              to="/challenges" 
              className="nav-link text-foreground/80 hover:text-primary font-medium hidden md:block"
              data-testid="challenges-nav"
            >
              GROWTH
            </Link>
            <Link 
              to="/saved" 
              className="nav-link text-foreground/80 hover:text-primary font-medium hidden md:block"
              data-testid="saved-plans-nav"
            >
              My Gardens
            </Link>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline font-medium">{user.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                    <Badge className="mt-1 capitalize" variant="secondary">
                      {user.subscription_tier === "free" ? "Free Plan" : 
                       user.subscription_tier === "standard" ? "The Gardener" : "Master Gardener"}
                    </Badge>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/saved')}>
                    <TreePine className="w-4 h-4 mr-2" />
                    My Gardens
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/identify')}>
                    <Camera className="w-4 h-4 mr-2" />
                    Identify Plant
                  </DropdownMenuItem>
                  {user.subscription_tier !== "free" && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/journal')}>
                        <BookOpen className="w-4 h-4 mr-2" />
                        Journal
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/seed-stock')}>
                        <Leaf className="w-4 h-4 mr-2" />
                        Seed Stock
                      </DropdownMenuItem>
                    </>
                  )}
                  {user.subscription_tier === "premium" && (
                    <DropdownMenuItem onClick={() => navigate('/pro-chat')}>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Pro Gardener Chat
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/design')}>
                    <Paintbrush className="w-4 h-4 mr-2" />
                    Visual Design Tool
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/learn')}>
                    <Trophy className="w-4 h-4 mr-2" />
                    Learning & Achievements
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                variant="outline"
                onClick={login}
                data-testid="login-btn"
              >
                Sign In
              </Button>
            )}
            
            <LanguageSelector variant="ghost" />
            
            {/* Help/Tour Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                localStorage.removeItem("rootspace_tour_completed");
                setShowTour(true);
              }}
              className="text-foreground/70 hover:text-primary"
              title="Take a tour"
              data-testid="help-tour-btn"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            
            <Button 
              onClick={() => navigate('/create')}
              className="btn-primary"
              data-testid="create-plan-nav-btn"
            >
              Create Plan
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          
          {/* Slide-out Menu */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="absolute left-0 top-0 bottom-0 w-72 bg-background shadow-xl"
            data-testid="mobile-menu-panel"
          >
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-menu-close-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Menu Links */}
            <nav className="p-4 space-y-1">
              {[
                { href: "/design", icon: Paintbrush, label: "Design Studio" },
                { href: "/plants", icon: Leaf, label: "Plant Database" },
                { href: "/educators", icon: BookOpen, label: "For Educators" },
                { href: "/blog", icon: Users, label: "Pro Blog" },
                { href: "/weather", icon: Sun, label: "Weather" },
                { href: "/contact", icon: MessageCircle, label: "Contact" },
                { href: "/marketplace", icon: Store, label: "Seed Shop" },
                { href: "/challenges", icon: Trophy, label: "GROWTH" },
                { href: "/saved", icon: TreePine, label: "My Gardens" },
              ].map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-primary/10 text-foreground/80 hover:text-primary transition-colors"
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
            </nav>
            
            {/* Menu Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-muted/30">
              <Button
                onClick={() => { setMobileMenuOpen(false); navigate('/create'); }}
                className="w-full btn-primary"
                data-testid="mobile-create-plan-btn"
              >
                Create Plan
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.pexels.com/photos/952485/pexels-photo-952485.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
            filter: 'brightness(0.5) saturate(1.2)'
          }}
        />
        
        <div className="absolute inset-0 bg-gradient-to-b from-green-900/40 via-transparent to-background" />
        <div className="hero-glow absolute top-1/4 left-1/2 -translate-x-1/2" />

        {/* Floating Side Navigation */}
        <div className="fixed left-4 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-2">
          {[
            { href: "/design", icon: Paintbrush, label: "Design Studio" },
            { href: "/plants", icon: Leaf, label: "Plant Database" },
            { href: "/educators", icon: BookOpen, label: "For Educators" },
            { href: "/blog", icon: Users, label: "Pro Blog" },
            { href: "/weather", icon: Sun, label: "Weather" },
            { href: "/contact", icon: MessageCircle, label: "Contact" },
          ].map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="group flex items-center gap-2 p-2 rounded-lg bg-white/90 hover:bg-primary hover:text-white shadow-lg transition-all duration-300"
              data-testid={`side-nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap text-sm font-medium">
                {item.label}
              </span>
            </Link>
          ))}
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          {/* Announcement Banner */}
          {siteSettings?.announcement_active && siteSettings?.announcement_banner && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 px-4 py-2 bg-secondary/90 text-secondary-foreground rounded-full inline-block"
            >
              {siteSettings.announcement_banner}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm mb-6">
              <Sparkles className="w-4 h-4" />
              Smart Permaculture Design
            </span>
            
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {siteSettings?.hero_title || "Plan Your Garden Paradise"}
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-4 font-light">
              {siteSettings?.hero_subtitle || "Smart garden planning powered by permaculture principles. Design sustainable, beautiful spaces that work with nature."}
            </p>
            
            <p className="text-base text-white/60 max-w-xl mx-auto mb-10 italic">
              Add curb appeal to your property while growing your own food
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/create')}
                className="btn-primary text-lg px-10 py-6"
                data-testid="hero-create-btn"
              >
                Start Planning
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/marketplace')}
                className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 rounded-full px-10 py-6"
                data-testid="hero-marketplace-btn"
              >
                <Store className="mr-2 w-5 h-5" />
                Seed Shop
              </Button>
            </div>
          </motion.div>

          <motion.div 
            className="flex flex-wrap justify-center gap-3 mt-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            {aesthetics.map((a, i) => (
              <motion.button 
                key={i}
                onClick={() => navigate(a.filter ? `/design?continent=${encodeURIComponent(a.filter)}` : '/design')}
                className={`inline-flex items-center gap-3 px-5 py-3 rounded-2xl ${a.color} text-sm font-bold cursor-pointer transition-all duration-300`}
                data-testid={`aesthetic-${a.name.toLowerCase().replace(' ', '-')}`}
                whileHover={{ scale: 1.08, y: -4 }}
                whileTap={{ scale: 0.95 }}
              >
                {a.icon}
                <div className="text-left">
                  <div className="font-bold">{a.name}</div>
                  <div className="text-xs opacity-90 font-normal">{a.desc}</div>
                </div>
              </motion.button>
            ))}
          </motion.div>
        </div>

        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-background">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-4">
              Everything You Need to <span className="text-secondary">Grow</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From solar calculations to companion planting, we&apos;ve thought of everything.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(feature.link)}
              >
                <Card 
                  className={`feature-card card-organic h-full cursor-pointer hover:scale-[1.02] transition-transform ${
                    hoveredFeature === index ? 'border-secondary shadow-lg' : ''
                  }`}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  data-testid={`feature-card-${index}`}
                >
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300 ${
                      hoveredFeature === index 
                        ? 'bg-secondary text-secondary-foreground' 
                        : 'bg-primary/10 text-primary'
                    }`}>
                      {feature.icon}
                    </div>
                    <h3 className="font-serif text-xl font-semibold mb-3 text-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tropical Food Growth Section - NEW VIBRANT */}
      <section 
        className="py-20 px-6 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${tropical.coconut.cream} 0%, ${tropical.mango.light}40 30%, ${tropical.green.vibrant}20 60%, ${tropical.papaya.light}30 100%)`
        }}
      >
        {/* Tropical Background Decorations */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl" style={{background: `${tropical.mango.vibrant}30`}} />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full blur-3xl" style={{background: `${tropical.green.vibrant}25`}} />
          <div className="absolute top-1/2 right-1/4 w-48 h-48 rounded-full blur-2xl" style={{background: `${tropical.plum.vibrant}15`}} />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span 
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-white text-sm font-bold mb-4"
              style={{background: `linear-gradient(135deg, ${tropical.mango.primary}, ${tropical.guava.primary})`}}
            >
              <span className="text-lg">🌴</span>
              Grow Tropical Food
            </span>
            <h2 
              className="font-serif text-4xl md:text-5xl font-bold mb-4"
              style={{
                background: `linear-gradient(135deg, ${tropical.green.dark}, ${tropical.mango.primary}, ${tropical.guava.primary})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              From Seed to Harvest
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Plan your tropical paradise with exotic fruits, vegetables, and herbs from around the world
            </p>
          </motion.div>

          {/* Tropical Food Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-10">
            {[
              { emoji: "🥭", name: "Mango", color: tropical.mango.primary },
              { emoji: "🍍", name: "Pineapple", color: tropical.mango.vibrant },
              { emoji: "🥥", name: "Coconut", color: "#8B7355" },
              { emoji: "🍌", name: "Banana", color: tropical.mango.light },
              { emoji: "🥑", name: "Avocado", color: tropical.green.primary },
              { emoji: "🍑", name: "Papaya", color: tropical.papaya.primary },
              { emoji: "🍊", name: "Citrus", color: "#FFA500" },
              { emoji: "🫐", name: "Acai", color: tropical.plum.primary },
              { emoji: "🥬", name: "Callaloo", color: tropical.green.vibrant },
              { emoji: "🌶️", name: "Scotch Bonnet", color: tropical.guava.primary },
              { emoji: "🍠", name: "Sweet Potato", color: tropical.papaya.primary },
              { emoji: "🫚", name: "Ginger", color: tropical.mango.light }
            ].map((food, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ scale: 1.1, y: -8 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center cursor-pointer shadow-lg hover:shadow-xl transition-all"
                style={{ boxShadow: `0 4px 20px ${food.color}25` }}
                onClick={() => navigate('/design')}
              >
                <span className="text-4xl block mb-2">{food.emoji}</span>
                <span className="text-sm font-semibold text-gray-700">{food.name}</span>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Button
              onClick={() => navigate('/design')}
              className="text-white font-bold px-8 py-6 text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all"
              style={{
                background: `linear-gradient(135deg, ${tropical.green.primary}, ${tropical.green.dark})`
              }}
            >
              <span className="mr-2">🌱</span>
              Start Growing Tropical
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-24 px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-4">
              Plans for Every <span className="text-primary">Gardener</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Start free and upgrade as your garden grows
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <Card className="card-organic">
                <CardContent className="p-6 text-center">
                  <h3 className="font-serif text-xl font-bold mb-2">The Planner</h3>
                  <p className="text-3xl font-bold text-primary mb-2">Free</p>
                  <p className="text-sm text-muted-foreground">1 garden plan</p>
                </CardContent>
              </Card>
              <Card className="card-organic border-secondary border-2 relative">
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary">Popular</Badge>
                <CardContent className="p-6 text-center">
                  <h3 className="font-serif text-xl font-bold mb-2">The Gardener</h3>
                  <p className="text-3xl font-bold text-primary mb-2">$7<span className="text-base font-normal">/mo</span></p>
                  <p className="text-sm text-muted-foreground">Unlimited plans</p>
                </CardContent>
              </Card>
              <Card className="card-organic">
                <CardContent className="p-6 text-center">
                  <h3 className="font-serif text-xl font-bold mb-2">Master Gardener</h3>
                  <p className="text-3xl font-bold text-primary mb-2">$19<span className="text-base font-normal">/mo</span></p>
                  <p className="text-sm text-muted-foreground">All features</p>
                </CardContent>
              </Card>
            </div>
            
            <Button
              onClick={() => navigate('/pricing')}
              className="btn-secondary"
              data-testid="view-pricing-btn"
            >
              View All Features
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Seed Marketplace Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-700 text-sm mb-4">
              <Store className="w-4 h-4" />
              Community Marketplace
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-4">
              Seed Shop
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Buy and sell heirloom seeds from fellow gardeners. Support seed diversity and connect with your community.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              onClick={() => navigate('/marketplace?filter=heirloom')}
              className="bg-white p-6 rounded-2xl shadow-sm border border-amber-100 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            >
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Leaf className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-serif text-xl font-bold mb-2">Heirloom & Organic</h3>
              <p className="text-muted-foreground text-sm">
                Find rare heirloom varieties and certified organic seeds from trusted sellers.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              onClick={() => navigate('/marketplace')}
              className="bg-white p-6 rounded-2xl shadow-sm border border-amber-100 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            >
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-serif text-xl font-bold mb-2">Seller Verified</h3>
              <p className="text-muted-foreground text-sm">
                Read reviews and ratings. Payment held until shipping is confirmed.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              onClick={() => navigate('/marketplace/seed-packet')}
              className="bg-white p-6 rounded-2xl shadow-sm border border-amber-100 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Camera className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-serif text-xl font-bold mb-2">Printable Packets</h3>
              <p className="text-muted-foreground text-sm">
                Design custom seed packets with our template. Ship with USPS labels.
              </p>
            </motion.div>
          </div>

          <div className="text-center">
            <Button
              size="lg"
              onClick={() => navigate('/marketplace')}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-10 py-6"
              data-testid="browse-seeds-btn"
            >
              <Store className="mr-2 w-5 h-5" />
              Browse Seeds
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Space?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-10 max-w-2xl mx-auto">
              Join thousands of gardeners creating sustainable, beautiful landscapes with smart permaculture design.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/create')}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-12 py-6 text-lg"
              data-testid="cta-create-btn"
            >
              Create Your Garden Plan
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-foreground text-background">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="RootSpace.Pro" className="h-10 w-auto brightness-0 invert" />
          </div>
          
          {/* Social Links */}
          {siteSettings?.social_links && (
            <div className="flex items-center gap-4">
              {siteSettings.social_links.instagram && (
                <a href={siteSettings.social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-background/60 hover:text-background transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
              )}
              {siteSettings.social_links.facebook && (
                <a href={siteSettings.social_links.facebook} target="_blank" rel="noopener noreferrer" className="text-background/60 hover:text-background transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
              )}
              {siteSettings.social_links.twitter && (
                <a href={siteSettings.social_links.twitter} target="_blank" rel="noopener noreferrer" className="text-background/60 hover:text-background transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
              )}
              {siteSettings.social_links.youtube && (
                <a href={siteSettings.social_links.youtube} target="_blank" rel="noopener noreferrer" className="text-background/60 hover:text-background transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              )}
              {siteSettings.social_links.tiktok && (
                <a href={siteSettings.social_links.tiktok} target="_blank" rel="noopener noreferrer" className="text-background/60 hover:text-background transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              )}
            </div>
          )}
          
          {/* Footer Links */}
          <div className="flex flex-wrap gap-4 md:gap-6 text-sm justify-center">
            <a href="/design" className="text-background/60 hover:text-background transition-colors">Design Tool</a>
            <a href="/plants" className="text-background/60 hover:text-background transition-colors">Plant Database</a>
            <a href="/educators" className="text-background/60 hover:text-background transition-colors">For Educators</a>
            <a href="/weather" className="text-background/60 hover:text-background transition-colors">Weather</a>
            <a href="/pricing" className="text-background/60 hover:text-background transition-colors">Pricing</a>
            <a href="/contact" className="text-background/60 hover:text-background transition-colors">Contact</a>
          </div>
          
          <p className="text-background/60 text-sm">
            {siteSettings?.footer_text || "© 2026 RootSpace.Pro. Sustainable gardens for a better world."}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
